import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Combobox, DatePicker } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { listPrograms, PROGRAM_CATALOG, PROGRAM_LIST_MAX } from '@/lib/events';
import {
  addDiscount,
  addOption,
  addProgram,
  removeDiscount,
  removeOption,
  removeProgram,
  renameOption,
  setValidity,
  updateProgram,
  type DiscountKind,
  type OptionTotals,
  type PricedProgram,
  type PricingResult,
  type PricingTotals,
  type ProposalPricing,
} from '../lib/api';
import { formatCount, formatDiscountAmount, formatMoney, toDateInput } from '../lib/money';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect } from '../ui/select';

export interface PricingPanelProps {
  readonly proposalId: string;
  readonly pricing: ProposalPricing;
  readonly totals: PricingTotals;
  /** Every mutation's result: the caller swaps in the re-projected document. */
  readonly onResult: (result: PricingResult) => void;
}

const DISCOUNT_KIND_LABEL: Record<DiscountKind, string> = { percent: '% off', fixed: '$ off' };

function positive(value: string): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function money(value: string): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : undefined;
}

type Run = () => Promise<PricingResult>;

interface ProgramRowProps {
  readonly program: PricedProgram;
  readonly busy: boolean;
  readonly onChange: (patch: { participants?: number; unitPrice?: number; flat?: boolean }) => void;
  readonly onRemove: () => void;
}

/** One program in an option: edit headcount and price (committed on blur/Enter), or drop it. */
function ProgramRow({ program, busy, onChange, onRemove }: ProgramRowProps) {
  const [participants, setParticipants] = useState(String(program.participants));
  const [unitPrice, setUnitPrice] = useState(String(program.unitPrice));

  const commit = () => {
    const nextParticipants = positive(participants);
    const nextPrice = money(unitPrice);
    const patch: { participants?: number; unitPrice?: number } = {};
    if (nextParticipants !== undefined && Math.round(nextParticipants) !== program.participants) {
      patch.participants = Math.round(nextParticipants);
    }
    if (nextPrice !== undefined && nextPrice !== program.unitPrice) patch.unitPrice = nextPrice;
    if (Object.keys(patch).length > 0) onChange(patch);
    else {
      setParticipants(String(program.participants));
      setUnitPrice(String(program.unitPrice));
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  return (
    <li className="rounded-md border border-border p-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{program.programName}</span>
        <span className="text-[13px] font-semibold tabular-nums">{formatMoney(program.total)}</span>
        <Button variant="ghost" size="icon" aria-label={`Remove ${program.programName}`} disabled={busy} onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Input
            type="number" min={1} step={1}
            aria-label={`Participants for ${program.programName}`}
            className="h-7 w-16 text-xs"
            value={participants} disabled={busy}
            onChange={(e) => setParticipants(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
          />
          <span className="whitespace-nowrap">people</span>
        </span>
        {program.basePrice !== undefined ? (
          // Base + per-person overage (BCE's "base for up to X, then per person").
          <span className="inline-flex flex-wrap items-center gap-1">
            <span className="whitespace-nowrap">· base {formatMoney(program.basePrice)} up to {program.includedParticipants ?? 0}, then $</span>
            <Input
              type="number" min={0} step={0.01}
              aria-label={`Per additional participant for ${program.programName}`}
              className="h-7 w-16 text-xs"
              value={unitPrice} disabled={busy}
              onChange={(e) => setUnitPrice(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
            />
            <span className="whitespace-nowrap">/person after</span>
          </span>
        ) : (
          <>
            <span aria-hidden="true">{program.flat ? '·' : '×'}</span>
            <span className="inline-flex items-center gap-1">
              <span>$</span>
              <Input
                type="number" min={0} step={0.01}
                aria-label={program.flat ? `Flat fee for ${program.programName}` : `Price per participant for ${program.programName}`}
                className="h-7 w-20 text-xs"
                value={unitPrice} disabled={busy}
                onChange={(e) => setUnitPrice(e.target.value)} onBlur={commit} onKeyDown={onKeyDown}
              />
              <span className="whitespace-nowrap">{program.flat ? 'flat fee' : 'each'}</span>
            </span>
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap">
              <input
                type="checkbox"
                checked={program.flat === true}
                disabled={busy}
                onChange={(e) => onChange({ flat: e.target.checked })}
              />
              Flat fee
            </label>
          </>
        )}
      </div>
    </li>
  );
}

interface OptionCardProps {
  readonly proposalId: string;
  readonly option: OptionTotals;
  readonly programOptions: readonly { readonly value: string; readonly label: string }[];
  readonly programsLoading: boolean;
  readonly busy: boolean;
  readonly run: (run: Run, after?: () => void) => void;
}

/** One option (package): its name, programs, discounts and net. */
function OptionCard({ proposalId, option, programOptions, programsLoading, busy, run }: OptionCardProps) {
  const [name, setName] = useState(option.name);
  const [programId, setProgramId] = useState('');
  const [participants, setParticipants] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [discountKind, setDiscountKind] = useState<DiscountKind>('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');

  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== option.name) run(() => renameOption(proposalId, option.id, trimmed));
    else setName(option.name);
  };

  const addProgramRow = (e: React.FormEvent) => {
    e.preventDefault();
    const p = positive(participants);
    const u = money(unitPrice);
    if (!programId || p === undefined || u === undefined) return;
    run(() => addProgram(proposalId, option.id, { programId, participants: Math.round(p), unitPrice: u }), () => {
      setProgramId('');
      setUnitPrice('');
    });
  };

  const addOff = (e: React.FormEvent) => {
    e.preventDefault();
    const value = positive(discountValue);
    if (value === undefined) return;
    const label = discountLabel.trim();
    run(() => addDiscount(proposalId, option.id, { kind: discountKind, value, ...(label ? { label } : {}) }), () => {
      setDiscountValue('');
      setDiscountLabel('');
    });
  };

  const canAddProgram = programId !== '' && positive(participants) !== undefined && money(unitPrice) !== undefined;

  return (
    <section className="space-y-2.5 rounded-lg border border-border bg-card p-2.5">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Option name"
          className="h-7 flex-1 border-transparent text-[13px] font-bold shadow-none hover:border-input focus-visible:border-input"
          value={name} disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <span className="text-[15px] font-bold tabular-nums">{formatMoney(option.net)}</span>
        <Button variant="ghost" size="icon" aria-label={`Remove ${option.name}`} disabled={busy} onClick={() => run(() => removeOption(proposalId, option.id))}>
          <Trash2 />
        </Button>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {option.participants > 0 ? `${formatCount(option.participants)} participants` : 'No programs yet'}
        {option.perPerson !== null ? ` · ${formatMoney(option.perPerson)} per person` : ''}
      </div>

      {option.programs.length > 0 ? (
        <ul className="space-y-1.5">
          {option.programs.map((program) => (
            <ProgramRow
              key={`${program.id}:${program.participants}:${program.unitPrice}:${program.flat ?? false}`}
              program={program}
              busy={busy}
              onChange={(patch) => run(() => updateProgram(proposalId, option.id, program.id, patch))}
              onRemove={() => run(() => removeProgram(proposalId, option.id, program.id))}
            />
          ))}
        </ul>
      ) : null}

      <form className="flex flex-wrap items-end gap-1.5 rounded-md border border-dashed p-2" onSubmit={addProgramRow}>
        <div className="min-w-[10rem] flex-1">
          <Label htmlFor={`prog-${option.id}`} className="text-[11px]">Add a program</Label>
          <Combobox
            id={`prog-${option.id}`}
            options={programOptions}
            value={programId}
            onChange={setProgramId}
            placeholder={programsLoading ? 'Loading…' : 'Search the catalog'}
            disabled={busy || programsLoading}
          />
        </div>
        <Input type="number" min={1} step={1} aria-label="Participants" placeholder="People" className="h-7 w-20 text-xs" value={participants} disabled={busy} onChange={(e) => setParticipants(e.target.value)} />
        <span className="pb-1.5 text-[11px] text-muted-foreground">×</span>
        <Input type="number" min={0} step={0.01} aria-label="Price per participant" placeholder="$ each" className="h-7 w-24 text-xs" value={unitPrice} disabled={busy} onChange={(e) => setUnitPrice(e.target.value)} />
        <Button type="submit" size="sm" disabled={busy || !canAddProgram}>Add</Button>
      </form>

      {option.discounts.length > 0 ? (
        <ul className="space-y-1">
          {option.discounts.map((discount) => (
            <li key={discount.id} className="flex items-center gap-2 rounded-md border p-1.5 text-[12px]">
              <span className="min-w-0 flex-1 truncate">{discount.label}</span>
              <span className="tabular-nums">{formatDiscountAmount(discount.amount)}</span>
              <Button variant="ghost" size="icon" aria-label={`Remove ${discount.label}`} disabled={busy} onClick={() => run(() => removeDiscount(proposalId, option.id, discount.id))}>
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <form className="flex flex-wrap items-center gap-1.5" onSubmit={addOff}>
        <NativeSelect aria-label="Discount type" className="h-7 w-20 text-xs" value={discountKind} disabled={busy} onChange={(e) => setDiscountKind(e.target.value as DiscountKind)}>
          {(Object.keys(DISCOUNT_KIND_LABEL) as DiscountKind[]).map((kind) => (
            <option key={kind} value={kind}>{DISCOUNT_KIND_LABEL[kind]}</option>
          ))}
        </NativeSelect>
        <Input type="number" min={0} step={discountKind === 'percent' ? 0.5 : 0.01} aria-label={discountKind === 'percent' ? 'Percent off' : 'Dollars off'} placeholder={discountKind === 'percent' ? '10' : '1000'} className="h-7 w-20 text-xs" value={discountValue} disabled={busy} onChange={(e) => setDiscountValue(e.target.value)} />
        <Input aria-label="Discount label" placeholder="Label (optional)" className="h-7 min-w-0 flex-1 text-xs" value={discountLabel} disabled={busy} onChange={(e) => setDiscountLabel(e.target.value)} />
        <Button type="submit" size="sm" variant="secondary" disabled={busy || positive(discountValue) === undefined}>Discount</Button>
      </form>

      {option.warnings.map((warning) => (
        <p key={warning} className="text-[11px] text-amber-700">{warning}</p>
      ))}
    </section>
  );
}

/**
 * The proposal's pricing: one or more options (packages), each a bundle of
 * programs with its own discounts and net event price. Presented side by side —
 * the client compares and picks. Every change goes to the API, which recomputes
 * the totals and re-projects the document; the result comes back via `onResult`.
 */
export function PricingPanel({ proposalId, pricing, totals, onResult }: PricingPanelProps) {
  const [error, setError] = useState<string | null>(null);

  const programs = useQuery({
    // Everything still sellable, not retired programs; the catalog is bigger than one default page.
    queryKey: ['programs', PROGRAM_CATALOG.active],
    queryFn: ({ signal }) => listPrograms('', signal, { catalog: PROGRAM_CATALOG.active, limit: PROGRAM_LIST_MAX }),
    staleTime: 60_000,
  });
  const programOptions = (programs.data?.programs ?? []).map((p) => ({
    value: p._id,
    label: p.displayName ?? p.name ?? p._id,
  }));

  const mutation = useMutation({
    mutationFn: (run: Run) => run(),
    onMutate: () => setError(null),
    onSuccess: (result) => onResult(result),
    onError: (err) => setError(errorMessage(err)),
  });
  const busy = mutation.isPending;
  const run = (fn: Run, after?: () => void) => mutation.mutate(fn, after ? { onSuccess: after } : undefined);

  return (
    <div className="space-y-4 p-3" aria-label="Pricing">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {totals.options.length <= 1 ? 'Net event price' : `${totals.options.length} options`}
        </div>
        {totals.options.length === 0 ? (
          <div className="text-[13px] text-muted-foreground">Add an option to start pricing.</div>
        ) : totals.net !== null ? (
          <div className="text-2xl font-bold tabular-nums">{formatMoney(totals.net)}</div>
        ) : (
          <div className="text-[13px] text-muted-foreground">
            {totals.options.map((o) => `${o.name}: ${formatMoney(o.net)}`).join(' · ')}
          </div>
        )}
      </div>

      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-[12px] text-destructive">
          {error}
        </p>
      ) : null}

      <div className="space-y-3">
        {totals.options.map((option) => (
          <OptionCard
            key={option.id}
            proposalId={proposalId}
            option={option}
            programOptions={programOptions}
            programsLoading={programs.isPending}
            busy={busy}
            run={run}
          />
        ))}
      </div>

      <Button variant="outline" size="sm" className="w-full" disabled={busy} onClick={() => run(() => addOption(proposalId))}>
        <Plus /> Add another option
      </Button>

      <section className="space-y-1 border-t pt-3">
        <Label htmlFor="pricing-valid-until" className="text-[12px] font-semibold">Pricing valid until</Label>
        <div className="flex items-center gap-1.5">
          <DatePicker
            id="pricing-valid-until"
            value={toDateInput(pricing.validUntil) || undefined}
            onChange={(value) => run(() => setValidity(proposalId, value || null))}
            disabled={busy}
            aria-label="Pricing valid until"
          />
          {pricing.validUntil ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => setValidity(proposalId, null))}>Clear</Button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
